/**
 * hotspotStatusLabel — geteilter Helper (F-SW7-11, Owner: SWEEP7-E4-FE-2, Winston §2.2)
 *
 * Wandelt rohe Backend-Sentinel-Werte ("unknown", "unkown" [BE-Tippfehler,
 * siehe features/hotspots/deviceType.ts], "not_found") in lesbare, über i18n
 * übersetzte Labels. Betroffene Felder sind u.a. `hotspot.device_type`
 * (z.B. /api/v2/hotspots liefert device_type: "not_found" für Hotspots ohne
 * Asset-Lookup) sowohl in der Hotspots-Liste als auch im Dashboard-Widget
 * "Top Hotspots" (/api/v2/dashboard → top_hotspots[].device_type).
 *
 * Wird konsumiert von:
 * - sweep7-e4-fe-2 (Owner) — features/hotspots/deviceType.ts, HotspotRow.tsx
 * - sweep7-e4-fe-1 (Konsument) — features/dashboard/TopHotspotsList.tsx
 * - sweep7-e4-fe-4 (Konsument, falls Status-Rohwerte auftauchen) — notifications/news
 *
 * Übersetzungen liegen im geteilten "common"-Namespace unter "rawStatus.*",
 * damit alle Konsumenten unabhängig vom eigenen Feature-Namespace denselben
 * Schlüsselsatz referenzieren (i18next löst "common:key" unabhängig vom
 * Namespace auf, an das die aufrufende useTranslation()-Instanz gebunden ist).
 */

/** Minimale Signatur einer i18next-Übersetzungsfunktion (namespace-unabhängig aufrufbar). */
export type TranslateFn = (key: string, options?: Record<string, unknown>) => string

/**
 * Bekannte rohe Sentinel-Werte → gemeinsamer i18n-Key im "common"-Namespace.
 * "unkown" ist ein bekannter BE-Tippfehler (kein FE-Tippfehler) und wird
 * bewusst mit auf denselben Key wie "unknown" gemappt.
 */
const RAW_STATUS_KEY_MAP: Record<string, string> = {
  unknown: 'common:rawStatus.unknown',
  unkown: 'common:rawStatus.unknown',
  not_found: 'common:rawStatus.notFound',
}

/** Prüft, ob ein roher Wert einer der bekannten Sentinel-Werte ist. */
export function isRawStatusSentinel(raw: string | null | undefined): boolean {
  if (!raw) return false
  return raw.toLowerCase() in RAW_STATUS_KEY_MAP
}

/**
 * Liefert ein lesbares, übersetztes Label für einen rohen Sentinel-Wert.
 * Nicht-Sentinel-Werte werden unverändert zurückgegeben (Aufrufer entscheiden
 * selbst über Capitalize-/Domain-Fallbacks für "echte" Werte, z.B. deviceType.ts).
 *
 * @param raw Roh-Wert vom Backend (z.B. hotspot.device_type)
 * @param t   i18next-Übersetzungsfunktion (beliebiger Namespace)
 */
export function hotspotStatusLabel(raw: string | null | undefined, t: TranslateFn): string {
  if (!raw || !raw.trim()) return t('common:rawStatus.unknown')
  const key = RAW_STATUS_KEY_MAP[raw.toLowerCase()]
  if (key) return t(key)
  return raw
}
